import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle, Clock, Lock, Users, Zap, HeartHandshake } from 'lucide-react';

const pillars = [
  { icon: Lock,          title: 'Security-First Mindset',     desc: 'Every solution we architect begins with a security review. We do not bolt on security — it is built in from day one.' },
  { icon: CheckCircle,   title: 'Delivery Accountability',    desc: 'We own timelines, not just tasks. Milestones are tracked, communicated, and met — no surprises.' },
  { icon: Clock,         title: 'Rapid Response',             desc: 'Our engagement model ensures you always have a dedicated point of contact with fast turnaround on critical issues.' },
  { icon: Users,         title: 'Cross-Domain Expertise',     desc: 'Our team spans security, networks, software, and design — giving you integrated thinking across every discipline.' },
  { icon: Zap,           title: 'Technology Pragmatism',      desc: 'We recommend what works for your context, not what is trendy. Best-fit over best-marketed.' },
  { icon: HeartHandshake,'title': 'Long-Term Partnership',    desc: 'We invest in understanding your business deeply. Most of our clients have been with us for years, not months.' },
];

export default function WhyUs() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="why-us" ref={ref} style={{ padding: '100px 5%', background: 'var(--navy-mid)', position: 'relative', overflow: 'hidden' }}>
      {/* Diagonal bg pattern */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 40px,
          rgba(0,172,193,0.04) 40px,
          rgba(0,172,193,0.04) 41px
        )`,
      }} />

      {/* Amber glow top right */}
      <div style={{ position: 'absolute', top: '5%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,122,0,0.08) 0%, transparent 70%)', zIndex: 0 }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 80, alignItems: 'start' }} className="why-grid">

          {/* Left sticky header */}
          <div>
            <motion.div initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
              <span style={{ fontFamily: 'Space Mono', fontSize: 11, letterSpacing: '0.25em', color: 'var(--amber)', textTransform: 'uppercase' }}>
                — Why Choose Us
              </span>
              <h2 style={{ fontSize: 'clamp(36px, 4vw, 54px)', fontWeight: 700, color: 'var(--text-main)', marginTop: 16, lineHeight: 1.1 }}>
                Built on <span style={{ color: 'var(--teal-dark)' }}>Trust</span>,<br />Delivered with <span style={{ color: 'var(--amber)' }}>Precision</span>.
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 16, marginTop: 24, lineHeight: 1.8, fontWeight: 300 }}>
                We measure our success by the outcomes we create for clients — not by the volume of engagements we take on.
              </p>

              {/* Divider */}
              <div style={{ width: 60, height: 3, background: 'linear-gradient(90deg, var(--teal), var(--amber))', marginTop: 36, borderRadius: 2 }} />

              {/* Quote */}
              <blockquote style={{ marginTop: 36, paddingLeft: 20, borderLeft: '2px solid rgba(0,172,193,0.35)', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 15, lineHeight: 1.8, fontWeight: 300 }}>
                "Reliability is not a feature — it is the foundation on which everything else is built."
              </blockquote>
            </motion.div>
          </div>

          {/* Right: pillars */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  style={{
                    background: '#ffffff', border: '1px solid var(--navy-border)',
                    borderRadius: 8, padding: '28px 24px',
                    boxShadow: '0 16px 40px rgba(15,23,42,0.05)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  whileHover={{ scale: 1.02 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,172,193,0.3)'; e.currentTarget.style.boxShadow = '0 22px 52px rgba(15,23,42,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--navy-border)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(15,23,42,0.05)'; }}>
                  <Icon size={22} color="var(--teal)" style={{ marginBottom: 16 }} />
                  <h4 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 17, color: 'var(--text-main)', marginBottom: 10 }}>{p.title}</h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: 13.5, lineHeight: 1.7, fontWeight: 300 }}>{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .why-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .why-grid > div:last-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
